import React from 'react';
import {
  Button,
  Input,
  Label,
  Textarea,
  VirtualizedFormSelect,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { DateField } from '../FormFields';
import { MarkdownContent } from '../MarkdownContent';
import {
  PRODUCT_UPDATE_NONE_PERMISSION,
  PRODUCT_UPDATE_PRIORITY_SELECT_OPTIONS,
  ProductUpdatePriority,
  isProductUpdatePriority,
  productUpdateAudienceLabel,
  productUpdatePriorityDescription,
} from '../../services/productUpdates';

export type EditorMode = 'create' | 'edit';

export type MarkdownEditorView = 'text' | 'preview';

export type EditorState = {
  mode: EditorMode;
  /** Set when editing; identifies the row being edited in the list. */
  id?: string;
  version: string;
  markdown: string;
  permissionSlug: string;
  featureFlagSlug: string;
  agencyIds: string[];
  priority: ProductUpdatePriority;
  /** YYYY-MM-DD; null means never expires. */
  expiresAt: string | null;
};

export type ProductUpdateSelectOption = {
  value: string;
  label: string;
};

export type ProductUpdateEditorFormProps = {
  editor: EditorState;
  setEditor: React.Dispatch<React.SetStateAction<EditorState | null>>;
  markdownEditorView: MarkdownEditorView;
  setMarkdownEditorView: React.Dispatch<
    React.SetStateAction<MarkdownEditorView>
  >;
  permissionSelectOptions: ProductUpdateSelectOption[];
  featureFlagSelectOptions: ProductUpdateSelectOption[];
  agencySelectOptions: ProductUpdateSelectOption[];
  permissionSlugToName: ReadonlyMap<string, string>;
  agencyIdToName: ReadonlyMap<string, string>;
  /** Maps markdown image file names to presigned URLs for preview rendering. */
  imageUrls: Record<string, string>;
  isFormValid: boolean;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
};

export const ProductUpdateEditorForm: React.FC<
  ProductUpdateEditorFormProps
> = ({
  editor,
  setEditor,
  markdownEditorView,
  setMarkdownEditorView,
  permissionSelectOptions,
  featureFlagSelectOptions,
  agencySelectOptions,
  permissionSlugToName,
  agencyIdToName,
  imageUrls,
  isFormValid,
  isSaving,
  onSave,
  onCancel,
}) => (
  <div className='space-y-4'>
    <h3 className='font-medium'>
      {editor.mode === 'create' ? 'New product update' : 'Edit product update'}
    </h3>
    <div className='grid gap-4 sm:grid-cols-2'>
      <div className='space-y-2'>
        <Label htmlFor='product-update-version'>Version</Label>
        <Input
          id='product-update-version'
          inputSize='sm'
          value={editor.version}
          onChange={e =>
            setEditor(prev =>
              prev ? { ...prev, version: e.target.value } : prev
            )
          }
          placeholder='may-21-2026'
        />
      </div>
      <div className='space-y-2'>
        <VirtualizedFormSelect
          key={`product-update-permission-${editor.permissionSlug}`}
          label='Permission'
          placeholder='Search permissions...'
          options={permissionSelectOptions}
          sortOptions
          selectSize='sm'
          isClearable={false}
          defaultValue={editor.permissionSlug}
          onChange={e =>
            setEditor(prev =>
              prev ? { ...prev, permissionSlug: e.target.value } : prev
            )
          }
          menuPortalTarget={null}
          menuPosition='absolute'
        />
        <p className='text-xs text-muted-foreground'>
          {productUpdateAudienceLabel(
            editor.permissionSlug === PRODUCT_UPDATE_NONE_PERMISSION
              ? null
              : editor.permissionSlug,
            permissionSlugToName
          )}
        </p>
      </div>
      <div className='space-y-2'>
        <VirtualizedFormSelect
          key={`product-update-feature-flag-${editor.featureFlagSlug}`}
          label='WorkOS feature flag'
          placeholder='Search feature flags...'
          options={featureFlagSelectOptions}
          sortOptions
          selectSize='sm'
          isClearable={false}
          defaultValue={editor.featureFlagSlug}
          onChange={e =>
            setEditor(prev =>
              prev ? { ...prev, featureFlagSlug: e.target.value } : prev
            )
          }
          menuPortalTarget={null}
          menuPosition='absolute'
        />
        <p className='text-xs text-muted-foreground'>
          Leave as all flags to show this entry regardless of WorkOS flag.
        </p>
      </div>
      <div className='space-y-2'>
        <Label htmlFor='product-update-agency'>Agency</Label>
        <VirtualizedMultiSelectCombobox
          options={agencySelectOptions}
          value={editor.agencyIds}
          onValueChange={ids =>
            setEditor(prev => (prev ? { ...prev, agencyIds: ids } : prev))
          }
          placeholder='All agencies'
          searchPlaceholder='Search agencies...'
        />
        <p className='text-xs text-muted-foreground'>
          {editor.agencyIds.length === 0
            ? 'All agencies'
            : `Agencies: ${editor.agencyIds
                .map(id => agencyIdToName.get(id) ?? id)
                .join(', ')}`}
        </p>
      </div>
      <div className='space-y-2'>
        <VirtualizedFormSelect
          key={`product-update-priority-${editor.priority}`}
          label='Priority'
          placeholder='Select priority...'
          options={PRODUCT_UPDATE_PRIORITY_SELECT_OPTIONS}
          selectSize='sm'
          isClearable={false}
          defaultValue={String(editor.priority)}
          onChange={e => {
            const priority = Number(e.target.value);
            setEditor(prev =>
              prev && isProductUpdatePriority(priority)
                ? { ...prev, priority }
                : prev
            );
          }}
          menuPortalTarget={null}
          menuPosition='absolute'
        />
        <p className='text-xs text-muted-foreground'>
          {productUpdatePriorityDescription(editor.priority)}. Lower numbers
          appear first; entries with the same priority sort newest first.
        </p>
      </div>
      <DateField
        fieldAnchor='product-update-expires-at'
        label='Expires on'
        htmlFor='product-update-expires-at'
        fieldSize='sm'
        value={editor.expiresAt}
        onChange={val =>
          setEditor(prev => (prev ? { ...prev, expiresAt: val } : prev))
        }
        yearRange={{
          fromYear: new Date().getFullYear(),
          toYear: new Date().getFullYear() + 10,
        }}
        extra={
          editor.expiresAt ? (
            <Button
              type='button'
              size='sm'
              variant='ghost'
              className='h-auto px-2 py-0 text-xs'
              onClick={() =>
                setEditor(prev => (prev ? { ...prev, expiresAt: null } : prev))
              }
            >
              Clear
            </Button>
          ) : null
        }
      />
      <p className='text-xs text-muted-foreground'>
        Leave empty for no expiry. Updates with an expiry on or before today are
        hidden from users and highlighted in red below.
      </p>
    </div>
    <div className='space-y-2'>
      <div
        className='inline-flex rounded-md border p-0.5'
        role='group'
        aria-label='Markdown editor view'
      >
        <Button
          type='button'
          size='sm'
          variant={markdownEditorView === 'text' ? 'default' : 'ghost'}
          aria-pressed={markdownEditorView === 'text'}
          onClick={() => setMarkdownEditorView('text')}
        >
          Text
        </Button>
        <Button
          type='button'
          size='sm'
          variant={markdownEditorView === 'preview' ? 'default' : 'ghost'}
          aria-pressed={markdownEditorView === 'preview'}
          onClick={() => setMarkdownEditorView('preview')}
        >
          Preview
        </Button>
      </div>
      {markdownEditorView === 'text' ? (
        <Textarea
          id='product-update-markdown'
          aria-label='Markdown content'
          textareaSize='default'
          className='min-h-[200px] font-mono text-sm'
          value={editor.markdown}
          onChange={e =>
            setEditor(prev =>
              prev ? { ...prev, markdown: e.target.value } : prev
            )
          }
          placeholder={"## What's new\n\n- Feature one"}
        />
      ) : (
        <div
          id='product-update-markdown-preview'
          className='min-h-[200px] rounded-md border bg-background px-3 py-2'
          aria-label='Markdown preview'
        >
          <MarkdownContent
            content={editor.markdown}
            imageUrls={imageUrls}
            emptyPlaceholder='Nothing to preview yet.'
          />
        </div>
      )}
    </div>
    <div className='flex gap-2'>
      <Button size='sm' onClick={onSave} disabled={!isFormValid || isSaving}>
        {isSaving && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
        Save
      </Button>
      <Button
        size='sm'
        variant='outline'
        onClick={onCancel}
        disabled={isSaving}
      >
        Cancel
      </Button>
    </div>
  </div>
);
